<template>
  <v-container class="status-page layout-spacing">
    <LoginTopbar :withLangSelector="false" class="mb-8" />

    <div v-if="isLoading" class="d-flex align-center justify-center">
      <AppLoader />
    </div>
    <template v-else>
      <!-- Overall Status -->
      <div class="overall-status font-weight-bold text-center text-white pa-5"
           :class="getStatusInfo(overallStatus).bgColor"
      >
        {{ getStatusInfo(overallStatus).label }}
      </div>

      <!-- Section About -->
      <v-container class="mt-8">
        <h2 class="mb-2">About This Site</h2>
        <p>
          Welcome to the Closd status page where you can find current information about the operational status and availability of our services.
        </p>
        <p class="mt-4">The site is for informational purposes only and not for contractual obligation purposes.</p>
        <p class="about-feedback text-grey-500 mt-4">
          Your feedback will help us improve this site. Please contact us at <a href="mailto:support@closd.com">support@closd.com</a>.
        </p>
      </v-container>

      <!-- Services -->
      <v-container class="mt-8">
        <v-card
          v-for="(service, name) in services"
          :key="name"
          flat
          class="d-flex align-center justify-space-between pa-4 service-card"
          :data-locator="`service-card-${name}`"
        >
          <div class="font-weight-medium">{{ service.label && service.label.trim() !== '' ? service.label : formatServiceName(name) }}</div>
          <div :class="getStatusInfo(service.status).color" data-locator="status">
            {{ getStatusInfo(service.status).label }}
          </div>
        </v-card>
      </v-container>
    </template>
  </v-container>
</template>

<script setup lang="ts">
import { AxiosError } from 'axios'
import { ref, onMounted } from 'vue'

import AppLoader from '@/common/AppLoader.vue'
import LoginTopbar from '@/login/LoginTopbar.vue'
import { AppStatusService, StatusApiResponse, StatusDetail } from '@/services/app-status.service'

const isLoading = ref<boolean>(true)
const overallStatus = ref<string>('UNKNOWN')
const services = ref<Record<string, StatusDetail>>({})

interface StatusInfo {
  label: string
  color: string
  bgColor: string
}

const STATUSES: Readonly<Record<string, StatusInfo>> = Object.freeze({
  UP: {
    label: 'Operational',
    color: 'text-success',
    bgColor: 'status-green',
  },
  DEGRADED: {
    label: 'Degraded',
    color: 'text-warning',
    bgColor: 'status-orange',
  },
  DOWN: {
    label: 'Outage',
    color: 'text-error',
    bgColor: 'status-red',
  },
  UNKNOWN: {
    label: 'Unknown status',
    color: 'text-grey',
    bgColor: 'status-grey',
  },
})

function getStatusInfo(status: string): StatusInfo {
  return STATUSES[status] ?? STATUSES['UNKNOWN']
}

function formatServiceName(name: string): string {
  return name.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

onMounted(async () => {
  try {
    const response = await AppStatusService.getStatus()
    const data: StatusApiResponse = response.data

    overallStatus.value = data['overall-status']
    services.value = data.details
  } catch (e) {
    const err = e as AxiosError<StatusApiResponse>
    const responseData = err.response?.data as StatusApiResponse | undefined
    if (responseData?.['overall-status'] && responseData?.details) {
      overallStatus.value = responseData['overall-status']
      services.value = responseData.details
    } else {
      overallStatus.value = 'DOWN'
      services.value = {
        'all-services': {
          label: 'All Services',
          status: 'DOWN',
          critical: true,
        },
      }
    }
    console.error('Error fetching app status:', e)
  } finally {
    isLoading.value = false
  }
})
</script>

<style scoped lang="scss">
.status-page {
  background: white;
  min-height: 100vh;
}

.overall-status {
  font-size: 1.2rem;
  border-radius: 4px;
}

.about-feedback {
  font-size: 0.9rem;
  a {
    text-decoration: none;
  }
}

.status {
  &-green { background-color: rgb(var(--v-theme-success)); }
  &-orange { background-color: rgb(var(--v-theme-warning)); }
  &-red { background-color: rgb(var(--v-theme-error-darken-1)); }
  &-grey { background-color: rgb(var(--v-theme-grey-lighten-1)); }
}

.service-card {
  border-bottom: 1px solid rgb(var(--v-theme-grey-100));
}
</style>
